import { Directive, Input, Renderer2 } from '@angular/core';
import { PermissionService } from '../../core/services/common/permission.service';
import { FieldPermissionStyleService } from '../services/field-permission-style.service';

@Directive()
export abstract class BaseFieldPermissionDirective {
  @Input() pageTarget: string = '';
  
  protected fieldGroups = {
    'address': [
      'zip', 'street', 'number', 'complement', 'neighborhood', 'city', 'state',
      'country', 'reference', 'address_type', 'address_line_1', 'address_line_2'
    ],
    'card_contacts': [
      'contact_name', 'contact_cpf', 'contact_email', 'contact_phone', 'contact_birthdate',
      'select_role_contact', 'remove_contact', 'add_contact'
    ]
  };
  
  constructor(
    protected permissionService: PermissionService,
    protected styleService: FieldPermissionStyleService
  ) {
    // Aplicar estilos globais uma vez durante a inicialização
    this.styleService.applyGlobalStyles();
  }
  
  /**
   * Verifica as permissões de um campo específico
   */
  protected checkFieldPermission(fieldName: string): { canView: boolean, canWrite: boolean } {
    if (this.permissionService.isAdminRole()) {
      return { canView: true, canWrite: true };
    }
    
    const fieldPermissions = this.permissionService.getFieldPermissions(this.pageTarget);
    
    if (!fieldPermissions || fieldPermissions.length === 0) {
      return { canView: true, canWrite: false };
    }
    
    const groupName = this.getGroupForField(fieldName);
    
    if (groupName) {
      const groupExists = fieldPermissions.some(
        (permission) => permission.permission_level === 'FIELD' && permission.target === groupName
      );
      
      if (!groupExists) {
        return { canView: true, canWrite: false };
      }
      
      const canWrite = this.permissionService.hasFieldPermission(
        this.pageTarget, groupName, 'write'
      );
      
      const canView = this.permissionService.hasFieldPermission(
        this.pageTarget, groupName, 'read'
      );
      
      return { canView, canWrite };
    } else {
      const fieldExists = fieldPermissions.some(
        (permission) => permission.permission_level === 'FIELD' && permission.target === fieldName
      );
      
      if (!fieldExists) {
        return { canView: true, canWrite: false };
      }
      
      const canWrite = this.permissionService.hasFieldPermission(
        this.pageTarget, 
        fieldName, 
        'write'
      );
      
      const canView = this.permissionService.hasFieldPermission(
        this.pageTarget, 
        fieldName, 
        'read'
      );
      
      return { canView, canWrite };
    }
  }
  
  /**
   * Aplica estilo somente leitura a um elemento
   */
  protected applyReadonlyStyleToElement(element: HTMLElement, renderer: Renderer2, tooltipMessage?: string): void {
    try {
      this.styleService.applyReadonlyStyle(element, renderer, tooltipMessage || 'Sem permissão para editar');
    } catch (error) {
      // Silenciar erros
    }
  }
  
  /**
   * Aplica estilo ofuscado a um elemento
   */
  protected applyObfuscatedStyleToElement(element: HTMLElement, renderer: Renderer2, tooltipMessage?: string): void {
    try {
      this.styleService.applyObfuscatedStyle(element, renderer, tooltipMessage || 'Sem permissão para visualizar');
    } catch (error) {
      // Silenciar erros
    }
  }
  
  protected getGroupForField(fieldName: string): string | null {
    for (const [groupName, fields] of Object.entries(this.fieldGroups)) {
      if (fields.includes(fieldName)) {
        return groupName;
      }
    }
    return null;
  }
}
